import { QueryResult, useQuery, ApolloError } from '@apollo/client';
import { isAfter, parseISO } from 'date-fns';
import config from '@monorepo/shared/src/config';
import { Classroom } from '@monorepo/shared/src/graph/schema-generated-types-educator';
import { StoreTypename } from '../../../types';
import GET_USER_CLASSROOMS from '../../queries/classrooms/get-user-classrooms';

export type UserClassroomState = {
  classrooms: Classroom[] | undefined;
  error: ApolloError | undefined;
  isLoading: boolean;
};

export type ClassroomSDispatch = {
  refetch: () => void;
};

export type ClassroomStore = [UserClassroomState, ClassroomSDispatch];

export const getDefaultUserStoreState = (): any => ({
  __typename: StoreTypename.USER_CLASSROOM,
});

const useUserClassrooms = (userId: number): ClassroomStore => {
  const { data, loading, error, refetch }: QueryResult<{ classrooms: Classroom[] | undefined }> =
    useQuery(GET_USER_CLASSROOMS, {
      variables: {
        userId: [userId],
        groupsWhere: {
          appId: 12,
        },
        studentLicensesWhere: {
          expired: false,
          productFamily: {
            code: config.productFamilyCode,
          },
        },
      },
    });

  const classrooms = [...(data?.classrooms || [])];

  const hasActiveMatManLicense = (student) =>
    student.licenses.some(
      ({ dateExpiration, product }) =>
        product.family.code === config.productFamilyCode &&
        isAfter(parseISO(dateExpiration), Date.now())
    );

  // used in filter drop down for reports and assigning letters
  const filterGroupEnrollments = (groups) =>
    groups.map(({ groupEnrollments, ...group }) => {
      if (!groupEnrollments?.length) return { groupEnrollments, ...group };

      const filteredGroupEnrollments = groupEnrollments.reduce(
        (acc, { student, ...groupEnrollment }) => {
          const hasLicense = hasActiveMatManLicense(student);
          if (!hasLicense) return acc;
          acc.push({ student, ...groupEnrollment });
          return acc;
        },
        [] as any
      );

      return { groupEnrollments: filteredGroupEnrollments, ...group };
    });

  const filteredClassrooms =
    classrooms.length &&
    classrooms.reduce((acc, { students, groups, ...classroom }) => {
      const hasLicensedStudent = students?.some((student) => hasActiveMatManLicense(student));
      if (!hasLicensedStudent) return acc;
      acc.push({
        ...classroom,
        students: students?.filter((student) => hasActiveMatManLicense(student)) || [],
        groups: filterGroupEnrollments(groups) || [],
      });
      return acc;
    }, [] as Classroom[]);

  return [
    {
      classrooms: filteredClassrooms || [],
      error,
      isLoading: loading,
    },
    { refetch },
  ];
};

export default useUserClassrooms;
